type FlipSoundElementLike = {
  data: unknown;
  load: () => Promise<boolean>;
  seek: (time: number) => void;
  seekPlay: (time: number) => void;
  restart: (time?: number) => void;
  pause: () => void;
  stop: () => void;
  playing: boolean;
  setPlaybackErrorHandler: (handler: ((error: unknown) => void) | null) => void;
  destroy: () => void;
  mediaDuration: number;
};

export type SharedFlipSoundElement = FlipSoundElementLike;

type GetSharedFlipSoundOptions = {
  content: string;
  createElement: () => FlipSoundElementLike;
};

class SharedFlipSoundElementProxy implements SharedFlipSoundElement {
  private readonly source: FlipSoundElementLike;

  constructor(source: FlipSoundElementLike) {
    this.source = source;
  }

  get data(): unknown {
    return this.source.data;
  }

  set data(value: unknown) {
    this.source.data = value;
  }

  get mediaDuration(): number {
    return this.source.mediaDuration;
  }

  get playing(): boolean {
    return this.source.playing;
  }

  load(): Promise<boolean> {
    return this.source.load();
  }

  seek(time: number): void {
    this.source.seek(time);
  }

  seekPlay(time: number): void {
    this.source.seekPlay(time);
  }

  restart(time = 0): void {
    this.source.restart(time);
  }

  pause(): void {
    this.source.pause();
  }

  stop(): void {
    this.source.stop();
  }

  setPlaybackErrorHandler(handler: ((error: unknown) => void) | null): void {
    this.source.setPlaybackErrorHandler(handler);
  }

  destroy(): void {
    this.source.pause();
  }

  destroyShared(): void {
    this.source.destroy();
  }
}

const sharedFlipSounds = new Map<string, SharedFlipSoundElementProxy>();
const sharedFlipSoundTasks = new Map<
  string,
  Promise<SharedFlipSoundElementProxy | null>
>();
const failedFlipSoundResources = new Set<string>();
let sharedFlipSoundGeneration = 0;

export async function getSharedFlipSoundElement({
  content,
  createElement
}: GetSharedFlipSoundOptions): Promise<SharedFlipSoundElement | null> {
  if (failedFlipSoundResources.has(content)) return null;

  const sharedFlipSound = sharedFlipSounds.get(content);
  if (sharedFlipSound) return sharedFlipSound;

  const sharedFlipSoundTask = sharedFlipSoundTasks.get(content);
  if (sharedFlipSoundTask) return sharedFlipSoundTask;

  const generation = sharedFlipSoundGeneration;
  const task = (async () => {
    const soundElement = createElement();
    soundElement.data = {
      id: 'flip-sound-shared',
      type: 'sound',
      content
    };

    const loaded = await soundElement.load();
    if (generation !== sharedFlipSoundGeneration) {
      soundElement.destroy();
      return null;
    }
    if (!loaded) {
      failedFlipSoundResources.add(content);
      soundElement.destroy();
      return null;
    }

    const proxy = new SharedFlipSoundElementProxy(soundElement);
    sharedFlipSounds.set(content, proxy);
    return proxy;
  })();

  sharedFlipSoundTasks.set(content, task);
  try {
    return await task;
  } finally {
    if (sharedFlipSoundTasks.get(content) === task) {
      sharedFlipSoundTasks.delete(content);
    }
  }
}

export function destroySharedFlipSoundElement(): void {
  sharedFlipSoundGeneration += 1;
  sharedFlipSounds.forEach((sound) => sound.destroyShared());
  sharedFlipSounds.clear();
  sharedFlipSoundTasks.clear();
  failedFlipSoundResources.clear();
}
